import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FilterAction from '../components/FilterActions';
import SurveyTable from '../components/home/SurveyTable';
import { ISurveyListingDataItem, TabPanelProps } from '../types/CommonTypes';
import { useAppDispatch, useAppSelector } from '../app/Hooks';
import { navbar } from '../redux/NavBarSlice';
import QuestionarieSurveyTable from '../components/QuestionarieSurveyTable';
import QuestionarieFilterActions from '../components/QuestionarieFilterActions';
import Loader from '../components/Loader';
import {
	resetSurveyPayload,
	getAudienceFilterData,
	configurationData,
	setModifiedSurveyResponse,
	setQuestionnaireStatus,
	surveyAllListingData,
} from '../redux/SurveySlice';
import { IconButton } from '../components/common/IconButton';
import { ReactComponent as PlusIcon } from '../assets/images/plus.svg';
import { setError, setHelperText } from '../redux/QuestionnaireSurveySlice';
import { questionnaireAPIKeys } from '../utils/ApiKeys';
import { addPageViewEventForGA } from '../utils/CommonMethods';

const StyledTab = styled(Tabs)({
	'.Mui-selected': {
		fontFamily: '"JohnsonText-Regular"',
		fontSize: '16px',
		fontWeight: '700',
		lineHeight: '16px',
		letterSpacing: '0px',
		textAlign: 'center',
		color: '#212121 !important',
		textTransform: 'none',
	},
});

const CustomTabPanel = (props: TabPanelProps) => {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`listing-page-tabpanel-${index}`}
			aria-labelledby={`listing-page-tab-${index}`}
			{...other}
		>
			{value === index && <Box>{children}</Box>}
		</div>
	);
};

const a11yProps = (index: number) => {
	return {
		id: `survey-tab-${index}`,
		'aria-controls': `survey-tabpanel-${index}`,
	};
};

const ListingPage: React.FC<unknown> = () => {
	const [value, setValue] = useState(0);
	const selectedNavBar = useAppSelector(navbar);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const configs = useAppSelector(configurationData);
	const surveyLegacyListData: ISurveyListingDataItem[] =
		useAppSelector(surveyAllListingData);
	const { t } = useTranslation();

	const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
		dispatch(resetSurveyPayload({}));
		const filterTypes = [
			'location',
			'status',
			'questionnaireName',
			'questionnaireAuthor',
		];
		filterTypes.forEach((filterType) => {
			dispatch(setError({ filterType: filterType, error: false }));
			dispatch(setHelperText({ filterType: filterType, helperText: '' }));
		});
	};

	useEffect(() => {
		addPageViewEventForGA(
			'pageview',
			'/',
			value === 0
				? questionnaireAPIKeys.HomeScreenAdmin
				: questionnaireAPIKeys.HomeScreenEndUser
		);
	}, [value]);

	useEffect(() => {
		configs?.rolePermissions?.isAdmin && dispatch(getAudienceFilterData());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [configs?.rolePermissions?.isAdmin]);

	return (
		<>
			<Loader isLoading={false}></Loader>
			<Box
				sx={{
					padding: {
						xs: '0px 10px',
						sm: '0px 16px',
						md: '0px 16px',
						lg: '0px 64px',
					},
				}}
			>
				{selectedNavBar === 0 && (
					<>
						<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
							<StyledTab
								value={value}
								onChange={handleChange}
								aria-label="Questionnaire Tab"
								TabIndicatorProps={{
									style: {
										backgroundColor: '#EB1700',
									},
								}}
							>
								<Tab
									role="tab"
									label={t('commonLabels.createdByMe')}
									{...a11yProps(0)}
									sx={{ fontFamily: '"JohnsonText-Medium"', color: '#212121' }}
								/>
								<Tab
									role="tab"
									label={t('commonLabels.allQuestionnaire')}
									{...a11yProps(1)}
									sx={{ fontFamily: '"JohnsonText-Medium"', color: '#212121' }}
								/>
							</StyledTab>
							{configs.rolePermissions?.isAdmin && (
								<Box sx={{ padding: '12px 8px 0px 0px' }}>
									<IconButton
										data-testid="icon-btn"
										style={{ width: '241px', height: '32px' }}
										iconType={<PlusIcon />}
										align="start"
										labelText={t('commonLabels.createNewQuestionnaire')}
										onClick={() => {
											dispatch(setModifiedSurveyResponse(surveyLegacyListData));
											dispatch(setQuestionnaireStatus(''));
											navigate('/create-survey');
										}}
									/>
								</Box>
							)}
						</Box>
						<CustomTabPanel value={value} index={0}>
							<FilterAction />
							<SurveyTable value={value} type={t('commonLabels.meText')} />
						</CustomTabPanel>

						<CustomTabPanel value={value} index={1}>
							<FilterAction />
							<SurveyTable value={value} type={t('commonLabels.allText')} />
						</CustomTabPanel>
					</>
				)}
				{selectedNavBar === 1 && (
					<>
						<Box>
							<QuestionarieFilterActions />
							<QuestionarieSurveyTable />
						</Box>
					</>
				)}
			</Box>
		</>
	);
};

export default ListingPage;
